/**
 * Text bindings, as pure functions: a UI Text names content fields as
 * `{Component.prop}` (`{Role.title}`), as Unity and Unreal bind properties to
 * data, so copy stays in the seed (rule 6) and a template is layout, not copy.
 * Values are formatted from the component schemas, never by name: a
 * `yearMonth` reads as the Inspector shows it, a list becomes paragraphs.
 *
 * What a binding resolves against (the Canvas's ancestors) is the caller's.
 */
import type { ComponentData, ComponentSchema } from "../../world/schema/defineComponent";
import type { PropType } from "../../world/schema/propTypes";
import { formatYearMonth } from "../format/dates";

export interface BindingSource {
  /** The nearest data for a component type, with its schema; null when none has it. */
  find(
    component: string,
  ): { readonly data: ComponentData; readonly schema: ComponentSchema } | null;
}

/** `{Component.prop}`: identifiers, as component types and prop keys are written. */
const TOKEN = /\{([A-Za-z]\w*)\.([A-Za-z]\w*)\}/g;
const LONE_TOKEN = /^\{([A-Za-z]\w*)\.([A-Za-z]\w*)\}$/;
/** How a list reads inside a sentence. */
const LIST_SEPARATOR = ", ";

/**
 * The paragraphs a Text shows: blank lines separate them, and each binding is
 * replaced by its value. A Text that is exactly one list binding shows each
 * item as its own paragraph. Paragraphs that come out empty (an absent
 * optional field) are left out.
 */
export function boundParagraphs(text: string, source: BindingSource): string[] {
  const list = loneList(text.trim(), source);
  const paragraphs = list ?? text.split(/\n\s*\n/).map((paragraph) => fill(paragraph, source));
  return paragraphs.map((paragraph) => paragraph.trim()).filter((paragraph) => paragraph !== "");
}

/** Whether a Text is just one list binding, so its paragraphs are the list's items (drawn as bullets). */
export function bindsList(text: string, source: BindingSource): boolean {
  return loneList(text.trim(), source) !== null;
}

/** A Text that is just one list binding, as its items; null for anything else. */
function loneList(text: string, source: BindingSource): string[] | null {
  const token = LONE_TOKEN.exec(text);
  if (!token) return null;
  const bound = lookup(source, token[1] ?? "", token[2] ?? "");
  if (bound?.type.type !== "array" || !Array.isArray(bound.value)) return null;
  const itemType = bound.type.of;
  return bound.value.map((item) => format(itemType, item));
}

function fill(paragraph: string, source: BindingSource): string {
  return paragraph.replace(TOKEN, (_match, component: string, prop: string) => {
    const bound = lookup(source, component, prop);
    // A binding with nothing to show says what it wanted, like a placeholder (rule 9).
    return bound ? format(bound.type, bound.value) : `[${component}.${prop}]`;
  });
}

function lookup(
  source: BindingSource,
  component: string,
  prop: string,
): { type: PropType; value: unknown } | null {
  const found = source.find(component);
  const type = found?.schema.props[prop];
  return found && type ? { type, value: found.data[prop] } : null;
}

/** A value as text, by its prop type. */
function format(type: PropType, value: unknown): string {
  if (type.type === "yearMonth") return formatYearMonth(value as string | null, type.nullLabel);
  if (type.type === "array") {
    return Array.isArray(value)
      ? value.map((item) => format(type.of, item)).join(LIST_SEPARATOR)
      : "";
  }
  if (type.type === "enum") {
    return type.options.find((option) => option.value === value)?.label ?? scalar(value);
  }
  return scalar(value);
}

/** Text, numbers and flags as written; anything else (absent, null, a vector) as nothing. */
function scalar(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}
